import React, { useEffect, useState } from "react";

import { fileUpload, handleEditorChange, incomplete, prepareEditorContentImage } from "utils";

import { LANGUAGES } from "consts";

import { AdminSection, Button, Col, Form, Input, Option, Row, Select, TextArea, TextEditor, UploadImage, Message as message } from "components";

export const CourseDetailsForm = ({ complete, course, isEditing, loading, methods, modules, setLoading, form }) => {
  const [description, setDescription] = useState("");
  const [headlineImageURL, setHeadlineImageURL] = useState("");
  const [headlineImageSuccess, setHeadlineImageSuccess] = useState(false);
  const [teacherImageSuccess, setTeacherImageSuccess] = useState(false);
  const [teacherImageURL, setTeacherImageURL] = useState("");

  const handleComplete = (v) => {
    if (!description) {
      message({
        content: "Please enter a description.",
        icon: "warning",
      });
      return;
    }

    complete({
      ...v,
      description,
      image: headlineImageURL,
      teacherImage: teacherImageURL,
    });
  };

  useEffect(() => {
    if (!isEditing || !course) {
      return;
    }
    setDescription(course.description);
    setHeadlineImageURL(course.image);
    setTeacherImageURL(course.teacherImage);
  }, [isEditing, course]);

  return loading ? null : (
    <>
      <AdminSection description="Update the course details here. Once the course has been added, you will be able to add course chapters and modules." />
      <Row className="midi-form">
        <Col>
          <Form form={form} name="confirm" onFinish={handleComplete} onFinishFailed={incomplete}>
            <Row>
              <Col>
                <Form.Item name="image" rules={[{ required: true }]} initialValue={course ? course.image : undefined}>
                  <UploadImage
                    imageSuccess={headlineImageSuccess}
                    imageUrl={headlineImageURL}
                    label="Add image"
                    beforeUpload={(v) => fileUpload(v, "courses", setHeadlineImageSuccess, setHeadlineImageURL, setLoading)}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col sm={24} md={16}>
                <Input name="title" label="Course title" rules={[{ required: true }]} initialValue={course ? course.title : ""} />
              </Col>
              <Col sm={24} md={4}>
                <Select name="language" rules={[{ required: true }]} label="Language" initialValue={course?.language}>
                  {LANGUAGES.filter((language) => language.showOnAdmin).map((language, index) => (
                    <Option key={index} value={language.code.toLowerCase()}>
                      {language.language}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col sm={24} md={4}>
                <Select name="active" rules={[{ required: true }]} label="Enabled" initialValue={course?.active}>
                  <Option value={true}>Enabled</Option>
                  <Option value={false}>Disabled</Option>
                </Select>
              </Col>
            </Row>
            <Row>
              <Col>
                <Input name="teaser" label="Course teaser" rules={[{ required: true }]} initialValue={course ? course.teaser : ""} />
              </Col>
            </Row>
            <Row>
              <Col>
                <Select
                  name="methods"
                  rules={[{ required: false }]}
                  style={{ width: "100%" }}
                  label="Select methods"
                  mode="tags"
                  initialValue={course && course.methods ? course.methods : undefined}
                >
                  {methods.map((item, i) => (
                    <Option value={item.id} key={i}>
                      {item.title}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col>
                <Select
                  name="modules"
                  rules={[{ required: false }]}
                  style={{ width: "100%" }}
                  label="Select modules"
                  mode="tags"
                  initialValue={course && course.modules ? course.modules : undefined}
                >
                  {modules.map((item, i) => (
                    <Option value={item.id} key={i}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>

            <AdminSection headline="Course description" />
            <Row>
              <Col>
                <TextEditor
                  init={{
                    images_upload_handler: (blobInfo, success) => {
                      prepareEditorContentImage({ file: blobInfo.blob(), location: "courses" }).then((res) => success(res));
                    },
                  }}
                  onEditorChange={(v) => handleEditorChange(v, setDescription)}
                  value={description}
                />
              </Col>
            </Row>

            <AdminSection headline="Course requirements" />
            <Row>
              <Col>
                <TextArea
                  name="requirements"
                  label="One per line"
                  rules={[{ required: true }]}
                  autoSize={{ minRows: 4 }}
                  initialValue={course ? course.requirements : ""}
                />
              </Col>
            </Row>

            <AdminSection headline="Course learnings" />
            <Row>
              <Col>
                <TextArea
                  name="learnings"
                  label="One per line"
                  rules={[{ required: true }]}
                  autoSize={{ minRows: 4 }}
                  initialValue={course ? course.learnings : ""}
                />
              </Col>
            </Row>

            <AdminSection headline="Teacher details" />
            <Row>
              <Col sm={24} md={19}>
                <Input name="teacherName" label="Teacher name" rules={[{ required: true }]} initialValue={course ? course.teacherName : ""} />
                <Input
                  name="teacherDescription"
                  label="Teacher description"
                  rules={[{ required: true }]}
                  className="margin-top-100"
                  initialValue={course ? course.teacherDescription : ""}
                />
              </Col>
              <Col sm={24} md={5}>
                <Form.Item name="image" rules={[{ required: true }]}>
                  <UploadImage
                    imageSuccess={teacherImageSuccess}
                    imageUrl={teacherImageURL}
                    initialValue={course ? course.teacherImage : undefined}
                    label="Add teacher image"
                    beforeUpload={(v) => fileUpload(v, "courses", setTeacherImageSuccess, setTeacherImageURL, setLoading)}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col>
                <Form.Item>
                  <Button type="primary" htmlType="submit" disabled={loading} loading={loading} className="margin-right-100">
                    Save Course
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </>
  );
};
